import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, Suspense } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import "./tailwind.css";
import "./App.css";
import { APPLICATION_ROUTES } from "./common/utils/routes";
import { describeError } from "./common/utils/error_message";
import { useAppContext } from "./app_context";
import { useUserDataContext } from "./user_data_context";
import Header from "./common/components/header";
import {
	isDesktopSidebarShown,
	getDesktopSidebarOffsetCss,
} from "./common/utils/functions/desktop_sidebar_offset";
import { DESKTOP_SIDEBAR_EASE } from "./common/components/header/header_styled_components";
import OfflineBanner from "./common/components/offline_banner";
import useWindowBreakpoint from "./hooks/use_window_breakpoint";
import UnreadAnswersNotifier from "./common/components/unread_answers_notifier";
import NotesRouteGuard from "./pages/user_profile/user_profile_notes/notes_route_guard";
import { useKeyboardVisibility } from "./hooks/use_keyboard_visibility";
import { useSendPendingQuestion } from "./hooks/use_send_pending_question";
import { useResumeAfterSignIn } from "./common/auth/use_resume_after_sign_in";
import { AuthModalProvider } from "./common/auth/auth_modal_provider";
import { fromResumePayload } from "./common/components/modal_windows/ask_question_modal_window/ask_question_resume";
import { getStoredFcmToken } from "./push/token_store";
import { QUESTION_PLACE } from "./common/constants/question";
import { useMentorModalTrigger } from "./hooks/use_mentor_modal_trigger";
import { useReferralMentor, MENTOR_REF_KEY } from "./hooks/use_referral_mentor";
import { BASE_API_URL } from "./common/endpoints";
import axios from "./axios_config";
import SpinnerLoader from "./common/components/loaders/spinner_loader";
import ErrorBoundary from "./common/components/error_boundary";
import lazyWithReload from "./common/utils/lazy_with_reload";
import { PROD_HOSTNAME } from "./common/utils/resolve_site_color_theme";
import { applySiteTheme } from "./common/utils/apply_site_theme";
import { isAppMenuShown } from "./common/utils/functions/app_menu";
import RedirectKeepQuery from "./common/components/redirect_keep_query";

const HomePage = lazyWithReload(() => import("./pages/home/home_page"));
const LandingPage = lazyWithReload(() => import("./pages/landing/landing_page"));
const DashboardPage = lazyWithReload(() => import("./pages/dashboard/dashboard_page"));
const StudyPage = lazyWithReload(() => import("./pages/study/study_page"));
const BibleNextPage = lazyWithReload(() => import("./pages/bible_next/bible_next_page"));
const GroupPage = lazyWithReload(() => import("./pages/group"));
const NotFoundPage = lazyWithReload(() => import("./pages/not_found_page"));
const OauthGmailPage = lazyWithReload(() => import("./pages/auth/oauth_gmail"));
const StudyPageCourses = lazyWithReload(() => import("./pages/study/study_page_courses"));
const StudyPageGroups = lazyWithReload(() => import("./pages/study/study_page_groups"));
const UserProfileQuestions = lazyWithReload(
	() => import("./pages/user_profile/user_profile_questions"),
);
const UserProfileActivity = lazyWithReload(
	() => import("./pages/user_profile/user_profile_activity"),
);
const UserProfileNotes = lazyWithReload(() => import("./pages/user_profile/user_profile_notes"));
const UserProfileNoteDetailPage = lazyWithReload(
	() => import("./pages/user_profile/user_profile_notes/user_profile_note_detail"),
);
const UserProfileFolderPage = lazyWithReload(
	() => import("./pages/user_profile/user_profile_notes/user_profile_folder_page"),
);
const UserProfileMentorship = lazyWithReload(
	() => import("./pages/user_profile/user_profile_mentorship"),
);
const PrivacyPolicyPage = lazyWithReload(() => import("./pages/legal/privacy_policy_page"));
const TermsOfServicePage = lazyWithReload(() => import("./pages/legal/terms_of_service_page"));
const AskQuestionModalWindow = lazyWithReload(
	() => import("./common/components/modal_windows/ask_question_modal_window"),
);
const QuestionSentModal = lazyWithReload(
	() => import("./common/components/modal_windows/question_sent_modal"),
);
const SupportModal = lazyWithReload(
	() => import("./common/components/modal_windows/support_modal"),
);
const RequestMentorModal = lazyWithReload(
	() => import("./common/components/modal_windows/request_mentor_modal"),
);
const ReferralResultModal = lazyWithReload(
	() => import("./common/components/modal_windows/referral_result_modal"),
);
const AboutPage = lazyWithReload(() => import("./pages/about/about_page"));
const DonatePage = lazyWithReload(() => import("./pages/donate/donate_page"));
const UserProfilePage = lazyWithReload(() => import("./pages/user_profile"));
const AuthPage = lazyWithReload(() => import("./common/auth/auth_page"));
const CoursePage = lazyWithReload(() => import("./pages/course"));
const UserLessonPage = lazyWithReload(() => import("./pages/user_lesson"));
const UserSharedLessonPage = lazyWithReload(() => import("./pages/user_shared_lesson"));
const RetiredSharedLinkPage = lazyWithReload(
	() => import("./pages/user_shared_lesson/retired_share_link_page"),
);
const SharedNotePage = lazyWithReload(() => import("./pages/shared_note"));
const ChatsPage = lazyWithReload(() => import("./pages/chats/chats_page"));

// Lazy load admin pages
const AdminPage = lazyWithReload(() => import("./pages/admin/admin_page"));
const AdminPageHome = lazyWithReload(() => import("./pages/admin/admin_page_home"));
const AdminPageQuestions = lazyWithReload(() => import("./pages/admin/admin_page_questions"));
const AdminPageCourses = lazyWithReload(() => import("./pages/admin/admin_page_courses"));
const AdminPageCreateLesson = lazyWithReload(
	() => import("./pages/admin/admin_page_create_lesson"),
);
const AdminPageCreateNotebook = lazyWithReload(
	() => import("./pages/admin/admin_page_create_notebook"),
);
const AdminPageUsers = lazyWithReload(() => import("./pages/admin/admin_page_users"));
const AdminPageAnalytics = lazyWithReload(() => import("./pages/admin/admin_page_analytics"));
const AdminPageFeedbacks = lazyWithReload(() => import("./pages/admin/admin_page_feedbacks"));
const AdminPageGroups = lazyWithReload(() => import("./pages/admin/admin_page_groups"));
const LessonPreviewPage = lazyWithReload(
	() => import("./pages/admin/admin_page_create_lesson/lesson_preview_page"),
);
const NotebookPreviewPage = lazyWithReload(
	() => import("./pages/admin/admin_page_create_notebook/notebook_preview_page"),
);

export const MENTOR_MODAL_MODE = {
	CLOSED: null,
	DEFAULT: "default",
	REMINDER: "reminder",
};

const AppContainer = ({ children }) => (
	<div className="flex flex-col overflow-hidden min-h-screen supports-[min-height:100dvh]:min-h-dvh max-lg:fixed max-lg:inset-0 max-lg:z-[1]">
		{children}
	</div>
);

export const AppContentContainer = ({
	headerAffectsHeight,
	isDesktop,
	desktopSidebarOffsetCss,
	children,
}) => (
	<div
		className="app-content-container min-h-0 flex-[1_1_auto] overflow-auto bg-bg-page dark:bg-bg-page"
		style={{
			// Same curve as the sidebar's width, so the page edge follows it exactly.
			transition: `margin-left ${DESKTOP_SIDEBAR_EASE}, width ${DESKTOP_SIDEBAR_EASE}, background-color 150ms ease`,
			marginLeft: isDesktop ? desktopSidebarOffsetCss : "0",
			width: isDesktop ? `calc(100% - ${desktopSidebarOffsetCss})` : "100%",
			"--app-header-h": headerAffectsHeight ? "var(--app-header-h-expanded)" : "0px",
		}}
	>
		{children}
	</div>
);

const LazyLoadFallback = ({ children }) => (
	<div className="flex justify-center items-center flex-1" style={{ height: "var(--vh)" }}>
		{children}
	</div>
);

function App() {
	const {
		isAppMenuExpanded,
		appMenuVisible,
		isSidebarCollapsed,
		isMentorRequestEnabled,
		isSettingsModalOpen,
		siteColorTheme,
	} = useAppContext();

	const { fetchUserData, unreadAnswersCount, userData, isAuthChecked } = useUserDataContext();
	// A question a guest wrote on the landing goes out once they are signed in.
	const pendingQuestion = useSendPendingQuestion();
	const [supportOpen, setSupportOpen] = useState(false);
	useResumeAfterSignIn("support", () => setSupportOpen(true));
	// Back from Google after the ask-question gate: the question window opens again, now with its form.
	const [resumedAsk, setResumedAsk] = useState(null);
	useResumeAfterSignIn("ask-question", payload => setResumedAsk(fromResumePayload(payload)));

	const [mentorModalMode, setMentorModalMode] = useState(MENTOR_MODAL_MODE.CLOSED);
	const [openAskQuestionModal, setOpenAskQuestionModal] = useState(false);

	useLayoutEffect(() => {
		applySiteTheme(siteColorTheme === "DARK");
	}, [siteColorTheme]);

	const { bindMentor, bindModalState, closeBindModal } = useReferralMentor();

	const location = useLocation();
	const { pathname } = location;
	const appMenuShown = isAppMenuShown(appMenuVisible, pathname);

	const isLowerHeader = useWindowBreakpoint(1024);
	const isDesktop = !isLowerHeader;
	const isAuthenticated = !!userData?._id;
	const headerAffectsHeight =
		appMenuShown && ((!isDesktop && isAppMenuExpanded) || (isDesktop && !isAuthenticated));
	// The rail is always on screen, so the page keeps its inset even while collapsed;
	// the hover-expanded panel overlays the content instead of pushing it.
	const desktopSidebarShown = isDesktopSidebarShown({
		isSidebarCollapsed,
		isDesktop,
		isAuthenticated,
		appMenuVisible: appMenuShown,
	});
	const desktopSidebarOffsetCss = getDesktopSidebarOffsetCss({
		isSidebarCollapsed,
		isDesktop,
		isAuthenticated,
		appMenuVisible: appMenuShown,
	});
	const isMentorModalOpen = mentorModalMode !== MENTOR_MODAL_MODE.CLOSED;

	const navigate = useNavigate();
	const isChatRoute = pathname === "/chats" || pathname.startsWith("/chats/");

	useKeyboardVisibility(isChatRoute || isSettingsModalOpen); // disabled in chat and when settings modal is open

	useLayoutEffect(() => {
		document.body.classList.toggle("has-sidebar", desktopSidebarShown);
	}, [desktopSidebarShown]);

	// for older versions of Safari/Chrome (mobile phones/tablets)
	useEffect(() => {
		// run fallback only when 100dvh is NOT supported
		if (CSS?.supports?.("height", "100dvh")) return;

		const vv = window.visualViewport;
		const root = document.documentElement;

		const update = () => {
			if (!vv) {
				root.style.setProperty("--real-vh", `${window.innerHeight}px`);
				return;
			}
			// iOS may shift the visual viewport instead of resizing it
			const availableHeight = vv.height + vv.offsetTop;

			// If keyboard clearly overlaps a lot, don't override layout height here
			const keyboardOverlay = window.innerHeight - availableHeight;
			if (keyboardOverlay > 150) return;

			root.style.setProperty("--real-vh", `${availableHeight}px`);
		};

		update();
		vv?.addEventListener("resize", update);
		vv?.addEventListener("scroll", update);
		const onOrient = () => setTimeout(update, 250);
		window.addEventListener("orientationchange", onOrient);

		return () => {
			vv?.removeEventListener("resize", update);
			vv?.removeEventListener("scroll", update);
			window.removeEventListener("orientationchange", onOrient);
		};
	}, []);

	// adds the class <html class="pwa-ios"> if iOS + PWA
	useEffect(() => {
		const isIOS = (() => {
			const ua = navigator.userAgent || "";
			const pf = navigator.platform || "";
			const iPadOS = pf === "MacIntel" && navigator.maxTouchPoints > 1;
			return /iPhone|iPad|iPod/.test(ua) || iPadOS;
		})();

		const isPWA = (() => {
			const mm = q => window.matchMedia?.(q).matches;
			return (
				!!window.navigator.standalone ||
				mm?.("(display-mode: standalone)") ||
				mm?.("(display-mode: fullscreen)") ||
				mm?.("(display-mode: minimal-ui)")
			);
		})();

		document.documentElement.classList.toggle("pwa-ios", isIOS && isPWA);
	}, []);

	useEffect(() => {
		fetchUserData();
	}, [fetchUserData]);

	// Capture ?mentor_ref param from URL and persist to sessionStorage
	const referralRedirected = useRef(false);
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const mentorRef = params.get("mentor_ref");
		if (mentorRef) {
			sessionStorage.setItem(MENTOR_REF_KEY, mentorRef);
			const url = new URL(window.location.href);
			url.searchParams.delete("mentor_ref");
			window.history.replaceState(null, "", url.toString());
			referralRedirected.current = false;
		}
	}, []);

	// Bind mentor as soon as the user is authenticated
	useEffect(() => {
		if (isAuthChecked && userData?._id) {
			bindMentor();
		}
	}, [isAuthChecked, userData?._id, bindMentor]);

	// Redirect unauthenticated user to login once if they have a pending referral.
	// Preserve current path as `?redirect=` so they land back where the referral link pointed
	// (the auth flow honours `?redirect=` after a successful login — see auth-flow.md).
	useEffect(() => {
		if (!isAuthChecked) return;
		if (userData?._id) return;
		if (referralRedirected.current) return;
		const token = sessionStorage.getItem(MENTOR_REF_KEY);
		if (token) {
			referralRedirected.current = true;
			const currentPath = `${location.pathname}${location.search}`;
			const redirectTarget =
				currentPath && currentPath !== "/" && !currentPath.startsWith(APPLICATION_ROUTES.LOGIN)
					? `${APPLICATION_ROUTES.LOGIN}?redirect=${encodeURIComponent(currentPath)}`
					: APPLICATION_ROUTES.LOGIN;
			navigate(redirectTarget, { replace: true });
		}
	}, [isAuthChecked, userData?._id, navigate, location.pathname, location.search]);

	useEffect(() => {
		let cancelled = false;
		let unsubscribe = null;

		// Firebase loads only on a device that opted into push; without a token nothing can arrive.
		if (!getStoredFcmToken()) return undefined;

		(async () => {
			let unsub = null;
			try {
				const { initForegroundPushListener } = await import("./push/init_foreground_push_listener");
				unsub = await initForegroundPushListener();
			} catch (e) {
				console.warn("Foreground push listener not loaded:", describeError(e));
			}
			if (cancelled) {
				if (typeof unsub === "function") unsub();
			} else {
				unsubscribe = unsub;
			}
		})();

		return () => {
			cancelled = true;
			if (typeof unsubscribe === "function") {
				unsubscribe();
			}
		};
	}, []);

	const closeMentorModal = useCallback(() => {
		setMentorModalMode(MENTOR_MODAL_MODE.CLOSED);
	}, []);

	const openMentorModal = useCallback(mode => {
		setMentorModalMode(mode);
	}, []);

	const updateUserMentorReminder = useCallback(
		async ({ remind_at }) => {
			if (!userData?._id) return;

			try {
				await axios.patch(BASE_API_URL + `/v1/users/me`, {
					user_mentor_reminder: {
						remind_at,
					},
				});

				fetchUserData();
			} catch (error) {
				console.error("[updateUserMentorReminder] Failed", {
					userId: userData._id,
					remind_at,
					error: describeError(error),
				});
			}
		},
		[userData?._id, fetchUserData],
	);

	useMentorModalTrigger({
		userData,
		isMentorRequestEnabled,
		openMentorModal,
		updateUserMentorReminder,
		enabled: !!userData?._id,
		isModalOpen: isMentorModalOpen,
	});

	return (
		<AuthModalProvider>
			<AppContainer>
				<>
					<OfflineBanner />

					<UnreadAnswersNotifier unreadAnswersCount={unreadAnswersCount} />

					{!isLowerHeader &&
						appMenuShown &&
						(isAuthenticated ? <Header /> : <Header forceTopHeader />)}
					<AppContentContainer
						headerAffectsHeight={headerAffectsHeight}
						isDesktop={isDesktop}
						desktopSidebarOffsetCss={desktopSidebarOffsetCss}
					>
						{/* Keyed by pathname: a crashed route recovers as soon as the user navigates away,
					    and the header stays mounted so they have somewhere to navigate to. */}
						<ErrorBoundary resetKey={location.pathname}>
							{/* One boundary for the lazy pages that have no Suspense of their own. */}
							<Suspense
								fallback={
									<LazyLoadFallback>
										<SpinnerLoader />
									</LazyLoadFallback>
								}
							>
								<Routes>
									<Route path="/" element={<HomePage />} />
									{/* Invite e-mails link to /home/?invite= — the query must survive the redirect. */}
									<Route path="/home" element={<RedirectKeepQuery to="/" />} />
									{/* Guest landing preview: never mounted on production. */}
									{window.location.hostname !== PROD_HOSTNAME && (
										<Route path={APPLICATION_ROUTES.WELCOME} element={<LandingPage />} />
									)}
									{window.location.hostname !== PROD_HOSTNAME && (
										<Route path={APPLICATION_ROUTES.DASHBOARD} element={<DashboardPage />} />
									)}

									<Route path={APPLICATION_ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
									<Route
										path={APPLICATION_ROUTES.TERMS_OF_SERVICE}
										element={<TermsOfServicePage />}
									/>
									<Route
										path={APPLICATION_ROUTES.ABOUT}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<AboutPage />
											</Suspense>
										}
									/>
									<Route path={APPLICATION_ROUTES.STUDY} element={<StudyPage />}>
										<Route path={APPLICATION_ROUTES.STUDY_GROUPS} element={<StudyPageGroups />} />
										<Route path={APPLICATION_ROUTES.STUDY_COURSES} element={<StudyPageCourses />} />
									</Route>

									<Route
										path={APPLICATION_ROUTES.STUDY_GROUPS + "/:groupId"}
										element={<GroupPage />}
									/>
									<Route
										path={APPLICATION_ROUTES.STUDY_COURSES + "/:courseId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<CoursePage />
											</Suspense>
										}
									/>
									{/* The old reader is gone; its links (?b=&bk=&ch=&v=) still circulate, and
							    the reader reads that shape itself (parseLegacyLink). */}
									<Route
										path={APPLICATION_ROUTES.OLD_BIBLE}
										element={
											<Navigate
												to={{ pathname: APPLICATION_ROUTES.BIBLE, search: location.search }}
												replace
											/>
										}
									/>
									<Route path={APPLICATION_ROUTES.BIBLE} element={<BibleNextPage />} />
									<Route
										path={`${APPLICATION_ROUTES.BIBLE}/:translation/:book/:chapter`}
										element={<BibleNextPage />}
									/>
									<Route
										path={`${APPLICATION_ROUTES.BIBLE}/:translation/:book/:chapter/:verse`}
										element={<BibleNextPage />}
									/>
									{/* /new-bible stays mounted for anyone with an old link — every
						    navigation from inside the reader now targets BIBLE, not this. */}
									<Route path={APPLICATION_ROUTES.BIBLE_NEXT} element={<BibleNextPage />} />
									<Route
										path={`${APPLICATION_ROUTES.BIBLE_NEXT}/:translation/:book/:chapter`}
										element={<BibleNextPage />}
									/>
									<Route
										path={`${APPLICATION_ROUTES.BIBLE_NEXT}/:translation/:book/:chapter/:verse`}
										element={<BibleNextPage />}
									/>
									<Route
										path={`${APPLICATION_ROUTES.CHATS}/*`}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<ChatsPage />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.DONATE}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<DonatePage />
											</Suspense>
										}
									/>

									{/* One page for all three: the tab follows the address. */}
									{[
										APPLICATION_ROUTES.AUTH,
										APPLICATION_ROUTES.LOGIN,
										APPLICATION_ROUTES.REGISTRATION,
									].map(path => (
										<Route
											key={path}
											path={path}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AuthPage />
												</Suspense>
											}
										/>
									))}

									<Route path={APPLICATION_ROUTES.OAUTH_GMAIL} element={<OauthGmailPage />} />

									<Route
										path="/profile/notes/new"
										element={
											<NotesRouteGuard>
												<UserProfileNoteDetailPage />
											</NotesRouteGuard>
										}
									/>
									<Route
										path="/profile/notes/folders/:folderId"
										element={
											<NotesRouteGuard>
												<UserProfileFolderPage />
											</NotesRouteGuard>
										}
									/>
									<Route
										path="/profile/notes/:noteId"
										element={
											<NotesRouteGuard>
												<UserProfileNoteDetailPage />
											</NotesRouteGuard>
										}
									/>

									<Route
										path={APPLICATION_ROUTES.PROFILE}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<UserProfilePage />
											</Suspense>
										}
									>
										<Route index element={<Navigate to="activity" replace />} />
										<Route path="questions" element={<UserProfileQuestions />} />
										<Route
											path="notes"
											element={
												<NotesRouteGuard>
													<UserProfileNotes />
												</NotesRouteGuard>
											}
										/>
										<Route path="activity" element={<UserProfileActivity />} />
										<Route path="mentorship" element={<UserProfileMentorship />} />
									</Route>

									<Route
										path={APPLICATION_ROUTES.ADMIN}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<AdminPage />
											</Suspense>
										}
									>
										<Route
											path={APPLICATION_ROUTES.ADMIN_HOME}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AdminPageHome />
												</Suspense>
											}
										/>
										<Route
											path={APPLICATION_ROUTES.ADMIN_COURSES}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AdminPageCourses />
												</Suspense>
											}
										/>
										<Route
											path={APPLICATION_ROUTES.ADMIN_GROUPS}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AdminPageGroups />
												</Suspense>
											}
										/>
										<Route
											path={APPLICATION_ROUTES.ADMIN_QUESTIONS}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AdminPageQuestions />
												</Suspense>
											}
										/>
										<Route
											path={APPLICATION_ROUTES.ADMIN_USERS}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AdminPageUsers />
												</Suspense>
											}
										/>
										<Route
											path={APPLICATION_ROUTES.ADMIN_ANALYTICS}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AdminPageAnalytics />
												</Suspense>
											}
										/>
										<Route
											path={APPLICATION_ROUTES.ADMIN_FEEDBACKS}
											element={
												<Suspense
													fallback={
														<LazyLoadFallback>
															<SpinnerLoader />
														</LazyLoadFallback>
													}
												>
													<AdminPageFeedbacks />
												</Suspense>
											}
										/>
									</Route>

									<Route
										path={APPLICATION_ROUTES.ADMIN_CREATE_LESSON + "/:lessonId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<AdminPageCreateLesson />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.ADMIN_CREATE_NOTEBOOK + "/:notebookId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<AdminPageCreateNotebook />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.ADMIN_LESSON_PREVIEW + "/:lessonId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<LessonPreviewPage />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.ADMIN_NOTEBOOK_PREVIEW + "/:notebookId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<NotebookPreviewPage />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.ADMIN_COURSE_PREVIEW + "/:courseId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<CoursePage inComponent={"admin_page"} />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.ADMIN_GROUP_PREVIEW + "/:groupId"}
										element={<GroupPage inComponent={"admin_page"} />}
									/>
									<Route
										path={APPLICATION_ROUTES.USER_LESSON + "/:lessonId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<UserLessonPage />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.USER_SHARED_LESSON + "/:token"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<UserSharedLessonPage />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.USER_SHARED_LESSON + "/:legacyUserId/:legacyLessonId"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<RetiredSharedLinkPage />
											</Suspense>
										}
									/>
									<Route
										path={APPLICATION_ROUTES.SHARED_NOTE + "/:token"}
										element={
											<Suspense
												fallback={
													<LazyLoadFallback>
														<SpinnerLoader />
													</LazyLoadFallback>
												}
											>
												<SharedNotePage />
											</Suspense>
										}
									/>
									<Route path="*" element={<NotFoundPage />} />
								</Routes>
							</Suspense>
						</ErrorBoundary>
					</AppContentContainer>
					{isLowerHeader && appMenuShown && <Header />}
				</>

				{isMentorModalOpen &&
					createPortal(
						<Suspense fallback={null}>
							<RequestMentorModal
								onClose={closeMentorModal}
								setOpenAskQuestionModal={setOpenAskQuestionModal}
							/>
						</Suspense>,
						document.body,
					)}

				{openAskQuestionModal &&
					createPortal(
						<Suspense fallback={null}>
							<AskQuestionModalWindow
								inComponent={QUESTION_PLACE.REQUEST_MENTOR}
								setOpenAskQuestionModal={setOpenAskQuestionModal}
							/>
						</Suspense>,
						document.body,
					)}

				{/* A landing question that went out after sign-in: the same "sent" window, with confetti. */}
				{pendingQuestion.sentOpen &&
					createPortal(
						<Suspense fallback={null}>
							<QuestionSentModal onClose={pendingQuestion.closeSent} />
						</Suspense>,
						document.body,
					)}

				{/* A guest who asked for support on the landing gets the support window once signed in. */}
				{supportOpen && (
					<Suspense fallback={null}>
						<SupportModal onClose={() => setSupportOpen(false)} />
					</Suspense>
				)}

				{resumedAsk &&
					createPortal(
						<Suspense fallback={null}>
							<AskQuestionModalWindow
								inComponent={resumedAsk.place}
								bibleVerses={resumedAsk.bibleVerses}
								courseInfoForQuestion={resumedAsk.courseInfoForQuestion}
								courseAuthors={resumedAsk.courseAuthors}
								setOpenAskQuestionModal={open => !open && setResumedAsk(null)}
							/>
						</Suspense>,
						document.body,
					)}

				{/* A landing question that failed to send: the modal opens with it from the draft. */}
				{pendingQuestion.retryOpen &&
					createPortal(
						<Suspense fallback={null}>
							<AskQuestionModalWindow
								inComponent={QUESTION_PLACE.HOME}
								setOpenAskQuestionModal={open => !open && pendingQuestion.closeRetry()}
							/>
						</Suspense>,
						document.body,
					)}

				{bindModalState &&
					createPortal(
						<Suspense fallback={null}>
							<ReferralResultModal
								type={bindModalState.type}
								mentorName={bindModalState.mentorName}
								onClose={closeBindModal}
							/>
						</Suspense>,
						document.body,
					)}
			</AppContainer>
		</AuthModalProvider>
	);
}

export default App;
